import { UserRoleType } from "@coeff/api";
import { AdminExtUser, DefaultApiGetUsersRequest } from "@coeff/api-admin-ext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { message } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey, SortColumn, SortDirection } from "../../../api";
import {
  AdminTableFooter,
  UserActionMenu,
  LoaderWithPerfTimings,
  SortDropdown,
  Typography,
  ConfirmBox,
  Button,
  Radio,
  Avatar,
  Alert,
  FlexColumn,
  FlexRow,
} from "../../../components";
import { AdminTable } from "../../../components/AdminTable";
import { AVATAR_COLORS, KEY_LABEL } from "../../../constants";
import { AxiosErrorWithMessage, getTimeZone, useApiContext } from "../../../utils";
import { useTableFilters } from "../useTableFilters";

import { getSortIcon } from "./DataImports";
import { DomainUsersFilterToolbar } from "./DomainUsersFilterToolbar";

const Container = styled.div`
  height: 100%;
  width: 100%;

  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  overflow: hidden;
  overflow-x: auto;
`;

const ROLES = {
  admin: "Admin",
  member: "Member",
} as const;

const PLANS = {
  pro: "Pro plan",
  starter: "Starter plan",
  plus: "Starter plan",
  enterprise: "Enterprise plan",
} as const;

const ROLE_METADATA = [
  {
    value: "admin",
    label: "Admin",
    tooltip: "Access to admin tools, billing management, plus all regular member features",
  },
  {
    value: "member",
    label: "Member",
    tooltip: "Regular access to Coefficient's features",
  },
] as const;

export const DomainUsers: React.FC = () => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const domain = searchParams.get("domain") ?? "all_domains";

  const { filters, setFilters } = useTableFilters<keyof DefaultApiGetUsersRequest>([
    "roles",
    "plans",
  ]);

  const [search, setSearch] = useState("");

  const { apiClient: api, adminExtApiClient, DEPRECATED_api } = useApiContext();

  const [sortedColumn, setSortedColumn] = useState<SortColumn>({
    columnKey: "name",
    value: SortDirection.ascend,
  });

  const [page, setPage] = React.useState(0);

  const [pageOffset, setPageOffset] = React.useState(0);

  const pageSize = 100;

  const [showManageRole, setShowManageRole] = useState<boolean>(false);

  const [user, setUser] = useState<AdminExtUser>();

  const [role, setRole] = useState<string>(UserRoleType.Member);

  const history = useHistory();

  const setPageAndOffset = (page: number) => {
    setPage(page);
    setPageOffset(page * pageSize);
  };

  const {
    data: domainUsersResponse,
    isLoading,
    refetch,
  } = useQuery(
    [QueryCacheKey.ADMIN_EXT_USERS, domain, pageOffset, pageSize, sortedColumn, filters, search],
    () =>
      adminExtApiClient.getUsers({
        domain,
        tz_name: getTimeZone(),
        page_size: pageSize,
        offset: pageOffset,
        sort_by: sortedColumn.columnKey,
        sort_order: sortedColumn.value,
        search,
        // filters
        roles: filters.roles as unknown as string,
        plans: filters.plans as unknown as string,
      })
  );

  const { data: hasOneAdmin, isLoading: allUsersLoading } = useQuery({
    queryKey: [QueryCacheKey.ADMIN_EXT_USERS, showManageRole],
    queryFn: async () => {
      const response = await adminExtApiClient.getUsers({
        domain,
        tz_name: getTimeZone(),
        roles: [UserRoleType.Admin] as unknown as string,
        plans: [] as unknown as string,
      });

      return response.data.users.length === 1;
    },
    enabled: showManageRole,
    cacheTime: 0,
    staleTime: 0,
    onError: () => {
      message.error("Failed to fetch users");
    },
  });

  const domainUsers = domainUsersResponse?.data?.users ?? [];

  const { mutate: handleUpdateUser, isLoading: isUpdatingRole } = useMutation({
    mutationFn: async ({ user, role }: { user: AdminExtUser; role: string }) => {
      await api.updateUserMe({
        update_user_me_request: {
          user: {
            user_role_type: role as UserRoleType,
            user_id: user.user_id,
          },
        },
      });

      await refetch();
    },
    onSuccess: () => {
      message.success("Role updated successfully");
      setShowManageRole(false);
    },
    onError: (error: AxiosErrorWithMessage) => {
      message.error(error.response.data.message || "Failed to update role");
    },
  });

  const columns: ColumnsType<AdminExtUser> = [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      showSorterTooltip: false,
      width: 400,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="name" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "name"),
      render: (name: string, user, index) => {
        return (
          <FlexRow gap={1.5} style={{ maxWidth: 400 }}>
            <Avatar
              style={{
                backgroundColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
                flexShrink: 0,
                height: 32,
                width: 32,
              }}
              iconSize="small"
            >
              {name
                .split(" ")
                .map(n => n.substring(0, 1).toUpperCase())
                .join("")}
            </Avatar>

            <FlexColumn gap={0} minWidthZero>
              <Typography noWrap title={name}>
                {name}
              </Typography>

              <Typography noWrap title={user.email} color="textSecondary">
                {user.email}
              </Typography>
            </FlexColumn>
          </FlexRow>
        );
      },
    },
    {
      title: KEY_LABEL.user_role_type,
      dataIndex: "user_role_type",
      key: "user_role_type",
      showSorterTooltip: false,
      width: 150,
      render: (name: string) => {
        return (
          <Typography noWrap title={ROLES[name]}>
            {ROLES[name]}
          </Typography>
        );
      },
    },
    {
      title: KEY_LABEL.plan_type,
      dataIndex: "plan_type",
      key: "plan_type",
      showSorterTooltip: false,
      width: 150,
      render: (name, user) => {
        return (
          <>
            {name ? (
              <Button
                type="link"
                noPadding
                style={{ height: "min-content" }}
                onClick={() =>
                  history.push(
                    `/dashboard/billing/subscription?subscription_id=${
                      // @ts-ignore
                      user.subscription_id.split("=")[1]
                    }`
                  )
                }
              >
                {PLANS[name]}
              </Button>
            ) : null}
          </>
        );
      },
    },
    {
      title: "Date Joined",
      dataIndex: "created_dt",
      key: "created_dt",
      width: 200,
      showSorterTooltip: false,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="created_dt" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "created_dt"),
      render: (createdDate: AdminExtUser["created_dt"]) =>
        createdDate ? (
          <Typography
            title={moment(createdDate).format("MMM D, YYYY h:mma")}
            noWrap
            fontSize="13px"
          >
            {moment(createdDate).fromNow()}
          </Typography>
        ) : (
          "-"
        ),
    },
    {
      title: "",
      showSorterTooltip: false,
      width: 40,
      render: (user: AdminExtUser) => {
        return (
          <UserActionMenu
            onUpdateRole={() => {
              setUser(user);
              setRole(user.user_role_type || UserRoleType.Member);
              setShowManageRole(true);
            }}
          />
        );
      },
    },
  ];

  const isLastAdminRoleChange = hasOneAdmin && user?.user_role_type === "admin";

  return (
    <Container style={{ maxWidth: "1000px" }}>
      <DomainUsersFilterToolbar
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        setFilters={setFilters}
      />

      <FlexColumn gap={0.5} style={{ overflow: "hidden" }}>
        <AdminTable
          columns={columns}
          dataSource={domainUsers ? domainUsers.map(d => ({ ...d, key: d.email })) : []}
          loading={false}
          locale={{
            emptyText: isLoading ? (
              <LoaderWithPerfTimings name="DomainUsers.locale" />
            ) : (
              "No users found"
            ),
          }}
          pagination={false}
        />

        <AdminTableFooter
          page={page}
          pageSize={pageSize}
          onPageChange={page => setPageAndOffset(page)}
          data={(domainUsers ?? []).map(x => [x.email, true])}
          totalRowCount={domainUsersResponse?.data.total_records ?? 0}
        />
      </FlexColumn>

      {showManageRole && user !== undefined ? (
        <ConfirmBox
          width={480}
          visible={showManageRole}
          title="Manage role"
          showCancel={false}
          okText="Save"
          okButton={
            !allUsersLoading && (
              <Button type="primary" disabled={isUpdatingRole || isLastAdminRoleChange}>
                {isUpdatingRole ? "Saving..." : "Save"}
              </Button>
            )
          }
          onCancel={() => setShowManageRole(false)}
          onOk={() => {
            if (role !== "admin" && hasOneAdmin) {
              return;
            }

            handleUpdateUser({ user, role });
          }}
          closable={true}
        >
          <FlexColumn>
            {allUsersLoading && <LoaderWithPerfTimings name="DomainUsers.allUsers" />}

            {!allUsersLoading && isLastAdminRoleChange && (
              <Alert
                type="info"
                showIcon
                message="Your domain must have at least one Admin. Make another user an Admin before changing."
              />
            )}

            {!allUsersLoading && (
              <Radio.Group
                style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                onChange={e => setRole(e.target.value)}
                value={role}
              >
                {ROLE_METADATA.map(item => (
                  <div
                    key={item.value}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      padding: "16px",
                      border: `1px solid ${item.value === role ? "#2563EB" : "#E5E5E5"}`,
                      borderRadius: "8px",
                      backgroundColor: item.value === role ? "#F8FAFF" : "#F5F5F5",
                    }}
                  >
                    <Radio
                      value={item.value}
                      disabled={isLastAdminRoleChange && item.value === "member"}
                    >
                      <div style={{ marginLeft: "8px" }}>
                        <div
                          style={{
                            fontWeight: 500,
                            color: item.value === role ? "#2563EB" : "inherit",
                          }}
                        >
                          <strong>{item.label}</strong>
                        </div>
                        <div style={{ color: "#6B7280", fontSize: "14px", marginTop: "4px" }}>
                          {item.tooltip}
                        </div>
                      </div>
                    </Radio>
                  </div>
                ))}
              </Radio.Group>
            )}
          </FlexColumn>
        </ConfirmBox>
      ) : null}
    </Container>
  );
};
